import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fileUrl } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search } from "lucide-react";

export default function PublicPlayers() {
  const { t } = useTranslation();
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState({});
  const [tournamentsList, setTournamentsList] = useState([]);
  const [q, setQ] = useState("");
  const [tournament, setTournament] = useState("all");
  const [category, setCategory] = useState("all");

  useEffect(() => {
    Promise.all([api.get("/players"), api.get("/teams"), api.get("/tournaments")]).then(([p, tm, tt]) => {
      setPlayers(p.data);
      setTeams(Object.fromEntries(tm.data.map((x) => [x.id, x])));
      setTournamentsList(tt.data);
    });
  }, []);

  const selectedTournament = tournamentsList.find((tt) => tt.id === tournament);
  const categoryOptions = selectedTournament?.categories || [];
  useEffect(() => { setCategory("all"); }, [tournament]);

  const filtered = useMemo(() => {
    return players.filter((p) => {
      if (q && !p.full_name.toLowerCase().includes(q.toLowerCase())) return false;
      const team = teams[p.team_id];
      const playerTids = (p.tournament_ids && p.tournament_ids.length ? p.tournament_ids : (team ? (team.tournament_ids && team.tournament_ids.length ? team.tournament_ids : (team.tournament_id ? [team.tournament_id] : [])) : []));
      if (tournament !== "all" && !playerTids.includes(tournament)) return false;
      if (category !== "all" && team?.category_id !== category) return false;
      return true;
    });
  }, [players, teams, q, tournament, category]);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
      <PageHeader title={t("players.title")} subtitle={t("players.subtitle")} testid="page-players" />
      <div className={`grid ${categoryOptions.length > 0 ? "md:grid-cols-3" : "md:grid-cols-2"} gap-3 mb-6`}>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <Input data-testid="players-search" placeholder={t("nav.players")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-navy-deep border-navy-border text-white rounded-sm" />
        </div>
        <Select value={tournament} onValueChange={setTournament}>
          <SelectTrigger data-testid="players-tournament-filter" className="bg-navy-deep border-navy-border text-white rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-navy-deep border-navy-border text-white">
            <SelectItem value="all">{t("games.filter_tournament_all")}</SelectItem>
            {tournamentsList.map((tour) => <SelectItem key={tour.id} value={tour.id}>{tour.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {categoryOptions.length > 0 && (
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="players-category-filter" className="bg-navy-deep border-navy-border text-white rounded-sm">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent className="bg-navy-deep border-navy-border text-white">
              <SelectItem value="all">Todas as categorias</SelectItem>
              {categoryOptions.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      {filtered.length === 0 ? (
        <div className="border border-dashed border-navy-border p-12 text-center text-ink-muted">{t("players.empty")}</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((p) => (
            <Link
              key={p.id}
              to={`/players/${p.id}`}
              data-testid={`player-card-${p.id}`}
              className="group bg-navy-deep border border-navy-border rounded-sm overflow-hidden hover:border-orange transition-all"
            >
              <div className="aspect-square bg-navy-surface relative overflow-hidden">
                {p.photo_url ? (
                  <img src={fileUrl(p.photo_url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                ) : (
                  <div className="w-full h-full grid place-items-center diagonal-stripes">
                    <span className="font-heading font-black text-7xl text-orange/40">{p.full_name?.[0]}</span>
                  </div>
                )}
                <span className="absolute top-2 left-2 bg-orange text-white font-mono tabular text-2xl font-black px-2 leading-none py-1">{p.jersey_number}</span>
              </div>
              <div className="p-3">
                <div className="font-heading font-bold uppercase tracking-tight text-white truncate group-hover:text-orange">{p.full_name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted truncate">{teams[p.team_id]?.name || "—"}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
